import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, MapPin, Mail, Phone, Youtube, Facebook, Type, FileText } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

export default function ContentManager({ siteSettings, loadData }) {
  const [contactInfo, setContactInfo] = useState({ email: "", phone: "", location: "" });
  const [socialLinks, setSocialLinks] = useState({ youtube: "", facebook: "" });
  const [heroText, setHeroText] = useState({ title: "", motto: "", subtitle: "" });
  const [aboutText, setAboutText] = useState({ history_p1: "", history_p2: "", history_p3: "", mission: "" });
  const [saving, setSaving] = useState("");

  useEffect(() => {
    if (siteSettings.contact_info) setContactInfo(siteSettings.contact_info);
    if (siteSettings.social_links) setSocialLinks(siteSettings.social_links);
    if (siteSettings.hero_text) setHeroText(siteSettings.hero_text);
    if (siteSettings.about_text) setAboutText(siteSettings.about_text);
  }, [siteSettings]); // eslint-disable-line react-hooks/exhaustive-deps -- setters are stable

  const saveSection = async (key, data) => {
    setSaving(key);
    try {
      await axios.put(`${API}/settings`, { [key]: data }, { withCredentials: true, headers: authHeaders() });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="space-y-8">
      {/* Contact Info */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-contact-info">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} className="text-[#E8922F]" />
          <h4 className="font-semibold text-[#1A1A1A]">Contact Information</h4>
        </div>
        <p className="text-[#4A4A4A] text-xs mb-4">Shown on the Contact page and Footer.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1"><Mail size={12} /> Email</Label>
            <Input value={contactInfo.email} onChange={e => setContactInfo({...contactInfo, email: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="contact-email-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1"><Phone size={12} /> Phone</Label>
            <Input value={contactInfo.phone} onChange={e => setContactInfo({...contactInfo, phone: e.target.value})} className="rounded-lg mt-1 text-sm" placeholder="+256 7XX XXX XXX" data-testid="contact-phone-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> Location</Label>
            <Input value={contactInfo.location} onChange={e => setContactInfo({...contactInfo, location: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="contact-location-setting" />
          </div>
        </div>
        <Button onClick={() => saveSection("contact_info", contactInfo)} className="btn-primary mt-4 text-sm" disabled={saving === "contact_info"} data-testid="save-contact-btn">
          <Save size={14} className="mr-1" /> {saving === "contact_info" ? "Saving..." : "Save Contact Info"}
        </Button>
      </div>

      {/* Social Links */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-social-links">
        <div className="flex items-center gap-2 mb-4">
          <Youtube size={18} className="text-[#E8922F]" />
          <h4 className="font-semibold text-[#1A1A1A]">Social Media Links</h4>
        </div>
        <p className="text-[#4A4A4A] text-xs mb-4">Shown in the Footer and Contact page.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1"><Youtube size={12} /> YouTube Channel URL</Label>
            <Input value={socialLinks.youtube} onChange={e => setSocialLinks({...socialLinks, youtube: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="youtube-link-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 flex items-center gap-1"><Facebook size={12} /> Facebook Page URL</Label>
            <Input value={socialLinks.facebook} onChange={e => setSocialLinks({...socialLinks, facebook: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="facebook-link-setting" />
          </div>
        </div>
        <Button onClick={() => saveSection("social_links", socialLinks)} className="btn-primary mt-4 text-sm" disabled={saving === "social_links"} data-testid="save-social-btn">
          <Save size={14} className="mr-1" /> {saving === "social_links" ? "Saving..." : "Save Social Links"}
        </Button>
      </div>

      {/* Hero Text */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-hero-text">
        <div className="flex items-center gap-2 mb-4">
          <Type size={18} className="text-[#E8922F]" />
          <h4 className="font-semibold text-[#1A1A1A]">Homepage Hero Text</h4>
        </div>
        <p className="text-[#4A4A4A] text-xs mb-4">The main text visitors see on the homepage.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Title</Label>
            <Input value={heroText.title} onChange={e => setHeroText({...heroText, title: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="hero-title-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Motto</Label>
            <Input value={heroText.motto} onChange={e => setHeroText({...heroText, motto: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="hero-motto-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Subtitle</Label>
            <Textarea value={heroText.subtitle} onChange={e => setHeroText({...heroText, subtitle: e.target.value})} className="rounded-lg mt-1 text-sm" rows={2} data-testid="hero-subtitle-setting" />
          </div>
        </div>
        <Button onClick={() => saveSection("hero_text", heroText)} className="btn-primary mt-4 text-sm" disabled={saving === "hero_text"} data-testid="save-hero-text-btn">
          <Save size={14} className="mr-1" /> {saving === "hero_text" ? "Saving..." : "Save Hero Text"}
        </Button>
      </div>

      {/* About Text */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-about-text">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-[#E8922F]" />
          <h4 className="font-semibold text-[#1A1A1A]">About Page Content</h4>
        </div>
        <p className="text-[#4A4A4A] text-xs mb-4">Edit the text on the About Us page.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">History - Paragraph 1</Label>
            <Textarea value={aboutText.history_p1} onChange={e => setAboutText({...aboutText, history_p1: e.target.value})} className="rounded-lg mt-1 text-sm" rows={3} data-testid="about-p1-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">History - Paragraph 2</Label>
            <Textarea value={aboutText.history_p2} onChange={e => setAboutText({...aboutText, history_p2: e.target.value})} className="rounded-lg mt-1 text-sm" rows={3} data-testid="about-p2-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">History - Paragraph 3</Label>
            <Textarea value={aboutText.history_p3} onChange={e => setAboutText({...aboutText, history_p3: e.target.value})} className="rounded-lg mt-1 text-sm" rows={3} data-testid="about-p3-setting" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Mission Statement</Label>
            <Textarea value={aboutText.mission} onChange={e => setAboutText({...aboutText, mission: e.target.value})} className="rounded-lg mt-1 text-sm" rows={3} data-testid="about-mission-setting" />
          </div>
        </div>
        <Button onClick={() => saveSection("about_text", aboutText)} className="btn-primary mt-4 text-sm" disabled={saving === "about_text"} data-testid="save-about-text-btn">
          <Save size={14} className="mr-1" /> {saving === "about_text" ? "Saving..." : "Save About Text"}
        </Button>
      </div>
    </div>
  );
}
